import { useMemo, useState, type FormEvent } from "react";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserProfile } from "../../auth/useUserProfile.ts";
import { OnboardingShell } from "../../components/onboarding/OnboardingShell.tsx";
import * as f from "../../components/onboarding/onboarding-form.styles.ts";
import { TOTAL_ONBOARDING_STEPS, getStepNumber } from "../../onboarding/steps.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

export function AgeGatePage(): JSX.Element {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { signOut } = useAuthSession();
  const { refetch } = useUserProfile();

  const [countryCode, setCountryCode] = useState("GH");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [declined, setDeclined] = useState(false);

  function handleCountryChange(value: string): void {
    setCountryCode(value.toUpperCase().slice(0, 2));
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!confirmed) {
      setError("Please confirm you meet the legal betting age before continuing.");
      return;
    }
    if (countryCode.length !== 2) {
      setError("Country code must be 2 letters (ISO format, e.g. GH).");
      return;
    }
    setError(null);
    setBusy(true);
    const { error: rpcError } = await supabase.rpc("attest_legal_age", {
      p_country_code: countryCode,
      p_confirmed_legal_age: true,
    });
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    await refetch();
    // AuthGate reads confirmed_legal_age === true and routes onward.
  }

  async function handleDecline(): Promise<void> {
    setError(null);
    setBusy(true);
    const { error: rpcError } = await supabase.rpc("attest_legal_age", {
      p_country_code: countryCode || "GH",
      p_confirmed_legal_age: false,
    });
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return;
    }
    setDeclined(true);
    setBusy(false);
  }

  if (declined) {
    return (
      <OnboardingShell
        stepNumber={getStepNumber("age")}
        totalSteps={TOTAL_ONBOARDING_STEPS}
        eyebrow="Cannot continue"
        title="You declined age confirmation"
        description={
          "STRIKZA can't open the rest of the product without an age attestation. You can " +
          "reconsider above, or sign out if you'd rather leave for now. Either choice is " +
          "recorded only in your own audit log — nothing is shared."
        }
      >
        <div style={f.buttonRow}>
          <button type="button" style={f.primaryBtn} onClick={() => setDeclined(false)}>
            I&apos;ll confirm
          </button>
          <button type="button" style={f.secondaryBtn} onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell
      stepNumber={getStepNumber("age")}
      totalSteps={TOTAL_ONBOARDING_STEPS}
      title="Confirm you are legally eligible"
      description={
        "STRIKZA is for eligible adults only. You must be 18 or the legal betting age in your " +
        "jurisdiction, whichever is higher. This step only unlocks the next onboarding screen — it " +
        "does not enable real-stake recommendations."
      }
    >
      <form style={f.formStack} onSubmit={handleConfirm} noValidate>
        {error ? <div style={f.message("error")}>{error}</div> : null}

        <div style={f.fieldGroup}>
          <label htmlFor="country" style={f.label}>
            Country (ISO 2-letter)
          </label>
          <input
            id="country"
            type="text"
            inputMode="text"
            autoComplete="country"
            required
            maxLength={2}
            value={countryCode}
            onChange={(e) => handleCountryChange(e.target.value)}
            style={f.input}
            aria-describedby="country-hint"
          />
          <span id="country-hint" style={f.labelHint}>
            e.g. GH, NG. Used only for legal-age attestation.
          </span>
        </div>

        <label style={f.checkboxRow}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={f.checkboxInput}
          />
          <span>
            I confirm I meet the legal betting age for my jurisdiction (18+ or higher where
            applicable).
          </span>
        </label>

        <div style={f.buttonRow}>
          <button type="submit" style={f.primaryBtn} disabled={busy}>
            {busy ? "..." : "Confirm and continue"}
          </button>
          <button type="button" style={f.secondaryBtn} disabled={busy} onClick={handleDecline}>
            Decline
          </button>
        </div>
      </form>
    </OnboardingShell>
  );
}
